import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldLocalized, Fieldgroup, TabView } from 'components/form';
import Side from 'components/view/side';
import ContentRecordTab from 'components/content/record-tab';
import ContentFields from 'components/content/fields';
import { slugify } from 'utils';
import { stripTags } from 'utils/string';

export default class ContentBlogForm extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object,
    openModal: PropTypes.func.isRequired,
  };

  static propTypes = {
    record: PropTypes.object,
    content: PropTypes.object,
    values: PropTypes.object,
    location: PropTypes.object,
    related: PropTypes.object,
    onAddBlogCategory: PropTypes.func,
    onDelete: PropTypes.func,
  };

  state = {
    charCounts: {},
  };

  constructor(props, context) {
    super(props, context);
    this.addCategoryInputRef = React.createRef();
  }

  onClickDelete(event) {
    const { title } = this.props.record;
    event.preventDefault();

    this.context.openModal('ConfirmDelete', {
      title: 'this blog',
      confirmInput: {
        description: `Enter the blog title to confirm`,
        value: title,
      },
      onConfirm: () => {
        this.props.onDelete();
      },
    });
  }

  onChangeSEOField = (event, value) => {
    if (event.target) {
      const { name } = event.target;

      this.setState((state) => ({
        charCounts: {
          ...state.charCounts,
          [name]: value.length,
        },
      }));
    }
  };

  onChangeAddCategory = (event) => {
    event.preventDefault();
    const value = event.target?.realValue;
    if (value?.new) {
      this.context.openModal('NewContentBlogCategory', {
        record: {
          title: value.new,
        },
        params: {
          onCreate: (category) => {
            this.props.onAddBlogCategory(category);
            // set new category.id to select
            const event = new Event('change', { bubbles: true });
            this.addCategoryInputRef.current.onChangeInput(event, category.id);
          },
          onCancel: () => {
            this.addCategoryInputRef.current.reset();
          },
        },
      });
    }
  };

  render() {
    const {
      content,
      values,
      record = {},
      location,
      related: { users, categories },
    } = this.props;

    const { charCounts } = this.state;

    return (
      <>
        <TabView value={location.query.tab} isDefault>
          <fieldset>
            <div className="view-body-section">
              <div className="view-section-content">
                <div className="row">
                  <FieldLocalized
                    type="text"
                    label="Blog title"
                    name="title"
                    defaultValue={record.title}
                    required={true}
                    className="span2"
                  />
                  <Field
                    type="text"
                    label="Slug"
                    name="slug"
                    help="Used to uniquely identify the blog in a storefront URL"
                    placeholder={slugify(values.title)}
                    defaultValue={record.slug}
                    required={Boolean(record.id)}
                    maxLength={70}
                    className="span2"
                  />
                </div>
                <div className="row">
                  <Field
                    type="toggle"
                    label="Published"
                    name="published"
                    help="A published blog is visible in the storefront"
                    defaultChecked={
                      record.published !== undefined ? record.published : true
                    }
                  />
                </div>
                <div className="row">
                  <FieldLocalized
                    type="editor"
                    name="content"
                    label="Blog content"
                    defaultValue={record.content}
                    localeValue={record.$locale}
                    className="span4"
                  />
                </div>
                <ContentFields
                  {...this.props}
                  zone="content"
                  collection="content/blogs"
                  models={content.models}
                  values={values}
                  currency={this.context.client.currency}
                />
              </div>
            </div>
          </fieldset>
          <Fieldgroup
            label="SEO details"
            className="normal"
            defaultActive={record.meta_title || record.meta_description}
          >
            <fieldset>
              <div className="view-body-section">
                <div className="view-section-content">
                  <FieldLocalized
                    type="text"
                    name="meta_title"
                    label={
                      <span>
                        Meta title{' '}
                        <span className="muted">
                          ({charCounts.meta_title || 0} of 70 chars)
                        </span>
                      </span>
                    }
                    help="Search engines typically display the first 70 characters of your blog title"
                    defaultValue={record.meta_title}
                    localeValue={record.$locale}
                    placeholder={values.title}
                    onChange={this.onChangeSEOField}
                  />
                  <FieldLocalized
                    type="textarea"
                    name="meta_description"
                    label={
                      <span>
                        Meta description{' '}
                        <span className="muted">
                          (
                          {charCounts.meta_description ||
                            stripTags(record.meta_description).length ||
                            0}{' '}
                          of 170 chars)
                        </span>
                      </span>
                    }
                    help="Search engines typically display the first 320 characters of your blog description"
                    defaultValue={record.meta_description}
                    localeValue={record.$locale}
                    placeholder={stripTags(
                      values.content?.replace(/<br>/g, ' '),
                    ).slice(0, 320)}
                    autoSize={true}
                    rows={3}
                    maxRows={20}
                    onChange={this.onChangeSEOField}
                  />
                </div>
              </div>
            </fieldset>
          </Fieldgroup>
        </TabView>
        <ContentRecordTab
          {...this.props}
          collection="content/blogs"
          view={record.id ? 'edit' : 'new'}
        />
        <Side>
          <div className="view-side-container">
            <section className="view-side-section">
              <div className="view-side-body">
                <Field
                  type="select"
                  label="Author"
                  name="author_id"
                  placeholder="Choose an author"
                  options={users?.results.filter((user) => user.name)}
                  defaultValue={record.author_id}
                  required={true}
                />
              </div>
            </section>
            <section className="view-side-section">
              <div className="view-side-body">
                <Field
                  type="select"
                  label="Category"
                  name="category_id"
                  placeholder={
                    categories?.results.count
                      ? 'Choose a category'
                      : 'Create a category'
                  }
                  options={categories?.results.map((category) => ({
                    value: category.id,
                    label: category.title,
                  }))}
                  defaultValue={record.category_id}
                  onChange={this.onChangeAddCategory}
                  newable={true}
                  required={true}
                  ref={this.addCategoryInputRef}
                />
              </div>
            </section>
            <section className="view-side-section">
              <div className="view-side-body">
                <Field
                  type="image"
                  label="Featured image"
                  name="image"
                  defaultValue={record.image}
                />
              </div>
            </section>
            <section className="view-side-section">
              <div className="view-side-body">
                <Field
                  type="tags"
                  label="Tags"
                  name="tags"
                  defaultValue={record.tags}
                />
              </div>
            </section>
          </div>
        </Side>
      </>
    );
  }
}
